<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

// ============================================================================
// 类型定义
// ============================================================================
type Placement =
  | 'top'
  | 'topLeft'
  | 'topRight'
  | 'bottom'
  | 'bottomLeft'
  | 'bottomRight'
  | 'left'
  | 'leftTop'
  | 'leftBottom'
  | 'right'
  | 'rightTop'
  | 'rightBottom';

type Trigger = 'hover' | 'click';

// ============================================================================
// Props & Emits
// ============================================================================
const props = withDefaults(
  defineProps<{
    /** 弹出位置 */
    placement?: Placement;
    /** 触发方式 */
    trigger?: Trigger;
    /** 受控模式下的显示状态 */
    open?: boolean;
    /** 弹出框与触发元素的间距 */
    offset?: number;
    /** 是否禁用 */
    disabled?: boolean;
    /** 鼠标移出后延迟关闭时间(ms) */
    mouseLeaveDelay?: number;
    /** 鼠标移入后延迟打开时间(ms) */
    mouseEnterDelay?: number;
    /** 弹出内容的自定义类名，用于外部 CSS 控制位置 */
    contentClass?: string;
    /** 是否显示箭头 */
    showArrow?: boolean;
  }>(),
  {
    placement: 'left',
    trigger: 'hover',
    open: undefined,
    offset: 8,
    disabled: false,
    mouseLeaveDelay: 100,
    mouseEnterDelay: 0,
    contentClass: '',
    showArrow: false,
  },
);

const emit = defineEmits<{
  (e: 'openChange', visible: boolean): void;
}>();

// ============================================================================
// 响应式状态
// ============================================================================
const triggerRef = ref<HTMLElement | null>(null);
const popoverRef = ref<HTMLElement | null>(null);
const internalVisible = ref(false);
// 初始化在屏幕外，避免首次渲染闪烁
const popoverStyle = ref<Record<string, string>>({
  position: 'fixed',
  top: '-9999px',
  left: '-9999px',
  zIndex: '9999',
});
// 是否已计算定位（用于控制显示动画）
const isPositioned = ref(false);

// 是否受控模式
const isControlled = computed(() => props.open !== undefined);

// 实际显示状态
const visible = computed(() => {
  if (props.disabled) return false;
  return isControlled.value ? props.open : internalVisible.value;
});

// 箭头方向（根据 placement 决定）
const arrowDirection = computed(() => {
  const placement = props.placement;
  if (placement.startsWith('top')) return 'bottom';
  if (placement.startsWith('bottom')) return 'top';
  if (placement.startsWith('left')) return 'right';
  if (placement.startsWith('right')) return 'left';
  return 'right';
});

// ============================================================================
// 定时器
// ============================================================================
let enterTimer: ReturnType<typeof setTimeout> | null = null;
let leaveTimer: ReturnType<typeof setTimeout> | null = null;

const clearTimers = () => {
  if (enterTimer) {
    clearTimeout(enterTimer);
    enterTimer = null;
  }
  if (leaveTimer) {
    clearTimeout(leaveTimer);
    leaveTimer = null;
  }
};

// ============================================================================
// 显示/隐藏逻辑
// ============================================================================
const setVisible = (val: boolean) => {
  if (props.disabled) return;

  if (!isControlled.value) {
    internalVisible.value = val;
  }
  emit('openChange', val);
};

const delaySetVisible = (val: boolean, delay: number) => {
  clearTimers();
  if (delay > 0) {
    const timer = setTimeout(() => {
      setVisible(val);
    }, delay);
    if (val) {
      enterTimer = timer;
    } else {
      leaveTimer = timer;
    }
  } else {
    setVisible(val);
  }
};

// ============================================================================
// 事件处理
// ============================================================================
const onMouseEnter = () => {
  if (props.trigger !== 'hover') return;
  clearTimers();
  delaySetVisible(true, props.mouseEnterDelay);
};

const onMouseLeave = () => {
  if (props.trigger !== 'hover') return;
  delaySetVisible(false, props.mouseLeaveDelay);
};

const onTriggerClick = () => {
  if (props.trigger !== 'click') return;
  setVisible(!visible.value);
};

const onClickOutside = (e: MouseEvent) => {
  if (props.trigger !== 'click') return;
  if (!visible.value) return;

  const target = e.target as Node;
  const isClickInside = triggerRef.value?.contains(target) || popoverRef.value?.contains(target);

  if (!isClickInside) {
    setVisible(false);
  }
};

// ============================================================================
// 位置计算
// ============================================================================
const updatePosition = () => {
  if (!triggerRef.value || !popoverRef.value) return;

  // 如果设置了 contentClass，由外部 CSS 控制位置
  if (props.contentClass) {
    popoverStyle.value = {
      position: 'fixed',
      top: 'auto',
      left: 'auto',
      zIndex: '9999',
    };
    return;
  }

  const triggerRect = triggerRef.value.getBoundingClientRect();
  const popoverRect = popoverRef.value.getBoundingClientRect();
  const { offset } = props;

  let top = 0;
  let left = 0;

  // 根据 placement 计算位置
  switch (props.placement) {
    // 左侧
    case 'left':
      top = triggerRect.top + (triggerRect.height - popoverRect.height) / 2;
      left = triggerRect.left - popoverRect.width - offset;
      break;
    case 'leftTop':
      top = triggerRect.top;
      left = triggerRect.left - popoverRect.width - offset;
      break;
    case 'leftBottom':
      top = triggerRect.bottom - popoverRect.height;
      left = triggerRect.left - popoverRect.width - offset;
      break;

    // 右侧
    case 'right':
      top = triggerRect.top + (triggerRect.height - popoverRect.height) / 2;
      left = triggerRect.right + offset;
      break;
    case 'rightTop':
      top = triggerRect.top;
      left = triggerRect.right + offset;
      break;
    case 'rightBottom':
      top = triggerRect.bottom - popoverRect.height;
      left = triggerRect.right + offset;
      break;

    // 顶部
    case 'top':
      top = triggerRect.top - popoverRect.height - offset;
      left = triggerRect.left + (triggerRect.width - popoverRect.width) / 2;
      break;
    case 'topLeft':
      top = triggerRect.top - popoverRect.height - offset;
      left = triggerRect.left;
      break;
    case 'topRight':
      top = triggerRect.top - popoverRect.height - offset;
      left = triggerRect.right - popoverRect.width;
      break;

    // 底部
    case 'bottom':
      top = triggerRect.bottom + offset;
      left = triggerRect.left + (triggerRect.width - popoverRect.width) / 2;
      break;
    case 'bottomLeft':
      top = triggerRect.bottom + offset;
      left = triggerRect.left;
      break;
    case 'bottomRight':
      top = triggerRect.bottom + offset;
      left = triggerRect.right - popoverRect.width;
      break;
  }

  // 边界检测与调整
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // 防止超出左边界
  if (left < 0) {
    left = offset;
  }
  // 防止超出右边界
  if (left + popoverRect.width > viewportWidth) {
    left = viewportWidth - popoverRect.width - offset;
  }
  // 防止超出上边界
  if (top < 0) {
    top = offset;
  }
  // 防止超出下边界
  if (top + popoverRect.height > viewportHeight) {
    top = viewportHeight - popoverRect.height - offset;
  }

  popoverStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    bottom: 'auto',
    right: 'auto',
    zIndex: '9999',
  };
};

// ============================================================================
// 生命周期
// ============================================================================

// 监听 popoverRef，确保 DOM 已挂载后再计算位置
watch(
  () => popoverRef.value,
  el => {
    if (el && visible.value) {
      // 使用 requestAnimationFrame 确保内容已渲染
      requestAnimationFrame(() => {
        updatePosition();
        // 再等一帧确保位置已应用
        requestAnimationFrame(() => {
          isPositioned.value = true;
        });
      });
    }
  },
  { flush: 'post' },
);

watch(
  visible,
  val => {
    if (val) {
      // 重置状态，等待 popoverRef 变化时触发定位
      isPositioned.value = false;
      // 重置位置到屏幕外
      popoverStyle.value = {
        position: 'fixed',
        top: '-9999px',
        left: '-9999px',
        zIndex: '9999',
      };
    } else {
      isPositioned.value = false;
    }
  },
  { flush: 'sync' },
);

onMounted(() => {
  document.addEventListener('click', onClickOutside);
  window.addEventListener('resize', updatePosition);
  window.addEventListener('scroll', updatePosition, true);
});

onUnmounted(() => {
  clearTimers();
  document.removeEventListener('click', onClickOutside);
  window.removeEventListener('resize', updatePosition);
  window.removeEventListener('scroll', updatePosition, true);
});
</script>

<template>
  <div class="float-popover" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
    <!-- 触发器 -->
    <div ref="triggerRef" class="float-popover__trigger" @click="onTriggerClick">
      <slot />
    </div>

    <!-- 弹出内容 -->
    <Teleport to="body">
      <div
        v-if="visible"
        ref="popoverRef"
        class="float-popover__content"
        :class="[contentClass, { 'has-arrow': showArrow, 'is-visible': isPositioned }]"
        :style="popoverStyle"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
      >
        <div
          v-if="showArrow"
          class="float-popover__arrow"
          :class="`float-popover__arrow--${arrowDirection}`"
        />
        <slot name="content" />
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
@use '@rendora/design/styles/mixins.scss' as *;

.float-popover {
  display: inline-block;
}

.float-popover__trigger {
  display: inline-flex;
}

.float-popover__content {
  position: relative;
  background-color: var(--x-back-1);
  border-radius: var(--x-radius-medium);
  box-shadow: var(--x-shadow);
  // 默认隐藏，等待定位计算完成后显示
  opacity: 0;
  transition: opacity 0.15s ease;

  &.is-visible {
    opacity: 1;
  }

  @include mobile {
    border-radius: responsive-size(12px);
    padding: responsive-size(12px);
    border: responsive-size(1px) solid var(--x-back-5);
  }
}

// ============================================================================
// 箭头样式
// ============================================================================
$arrow-size: 8px;

.float-popover__arrow {
  position: absolute;
  width: 0;
  height: 0;
  border-style: solid;

  // 箭头指向右侧（popover 在左边）
  &--right {
    right: calc(-1 * $arrow-size);
    top: 50%;
    transform: translateY(-50%);
    border-width: $arrow-size 0 $arrow-size $arrow-size;
    border-color: transparent transparent transparent var(--x-back-1);

    // 箭头边框
    &::before {
      content: '';
      position: absolute;
      right: 0;
      top: calc(-1 * $arrow-size);
      border-width: $arrow-size 0 $arrow-size $arrow-size;
      border-style: solid;
      border-color: transparent transparent transparent var(--x-back-1);
      z-index: -1;
    }
  }

  // 箭头指向左侧（popover 在右边）
  &--left {
    left: calc(-1 * $arrow-size);
    top: 50%;
    transform: translateY(-50%);
    border-width: $arrow-size $arrow-size $arrow-size 0;
    border-color: transparent var(--x-back-1) transparent transparent;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: calc(-1 * $arrow-size);
      border-width: $arrow-size $arrow-size $arrow-size 0;
      border-style: solid;
      border-color: transparent var(--x-back-1) transparent transparent;
      z-index: -1;
    }
  }

  // 箭头指向下方（popover 在上边）
  &--bottom {
    bottom: calc(-1 * $arrow-size);
    left: 50%;
    transform: translateX(-50%);
    border-width: $arrow-size $arrow-size 0 $arrow-size;
    border-color: var(--x-back-1) transparent transparent transparent;

    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: calc(-1 * $arrow-size);
      border-width: $arrow-size $arrow-size 0 $arrow-size;
      border-style: solid;
      border-color: var(--x-back-1) transparent transparent transparent;
      z-index: -1;
    }
  }

  // 箭头指向上方（popover 在下边）
  &--top {
    top: calc(-1 * $arrow-size);
    left: 50%;
    transform: translateX(-50%);
    border-width: 0 $arrow-size $arrow-size $arrow-size;
    border-color: transparent transparent var(--x-back-1) transparent;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: calc(-1 * $arrow-size);
      border-width: 0 $arrow-size $arrow-size $arrow-size;
      border-style: solid;
      border-color: transparent transparent var(--x-back-1) transparent;
      z-index: -1;
    }
  }

  @include mobile {
    display: none;
  }
}
</style>
